import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import Header from "@/components/Header";
import ResearchPane from "@/components/ResearchPane";
import CasesPane from "@/components/CasesPane";
import { useDocuments, useModels } from "@/hooks/queries";
import { toast } from "sonner";

const VALID_SECTIONS = new Set(["research", "cases"]);

export default function LegalWorkspace() {
  const location = useLocation();
  const navigate = useNavigate();
  const { section } = useParams();
  const activeTab = useMemo(
    () => (VALID_SECTIONS.has(section) ? section : null),
    [section],
  );
  const [model, setModel] = useState("gemini-2.5-flash-lite");

  const { data: documents = [], error: docsError } = useDocuments();
  const { data: modelsData } = useModels();

  useEffect(() => {
    if (docsError) toast.error(docsError.message);
  }, [docsError]);

  useEffect(() => {
    if (modelsData?.default) setModel(modelsData.default);
  }, [modelsData?.default]);

  useEffect(() => {
    const tab = location.state?.tab;
    if (tab === "search" || tab === "library") {
      navigate("/workspace/research", { replace: true });
    } else if (tab === "chat" || tab === "draft") {
      navigate("/workspace/cases", { replace: true });
    } else if (tab && VALID_SECTIONS.has(tab) && tab !== section) {
      navigate(`/workspace/${tab}`, { replace: true });
    }
  }, [location.state, navigate, section]);

  const handleTabChange = (tab) => {
    navigate(`/workspace/${tab}`);
  };

  if (!activeTab) {
    if (section === "library") {
      return <Navigate to="/workspace/research" replace />;
    }
    if (section === "chat" || section === "draft") {
      return <Navigate to="/workspace/cases" replace />;
    }
    return <Navigate to="/workspace" replace />;
  }

  const renderMainPane = () => {
    if (activeTab === "research") {
      return <ResearchPane model={model} />;
    }
    if (activeTab === "cases") {
      return <CasesPane documents={documents} />;
    }
    return null;
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header activeTab={activeTab} onTabChange={handleTabChange} />

      <main className="flex-1 h-[calc(100vh-64px)] overflow-hidden">
        {renderMainPane()}
      </main>
    </div>
  );
}
